import { ContentListShell } from "@/components/studio/content-list-shell";
import { LifecycleActionsMenu } from "@/components/studio/lifecycle-actions-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useContentLifecycle } from "@/hooks/use-content-lifecycle";
import { ContentBlogService } from "@/lib/api/blog.service";
import { categoryService } from "@/lib/api/categories.service";
import { coachService } from "@/lib/api/coachService";
import { ContentTagService } from "@/lib/api/content.tag.service";
import { useAuth } from "@/lib/auth";
import type {
  Category,
  Coach,
  StudioContentStatus,
  TagGroup,
  TagOption,
} from "@/lib/schema";
import {
  CONTENT_TYPE_CONFIG,
  formatDate,
  STATUS_CONFIG,
} from "@/lib/studio-constants";
import { useQuery } from "@tanstack/react-query";
import { FileText, Globe, Tags, User } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";

const blogConfig = CONTENT_TYPE_CONFIG.blog;
const PAGE_SIZE = 5;
const ELEVATED_ROLES = [
  "admin",
  "super_admin",
  "coach_manager",
  "content_manager",
];

interface BlogListItem {
  id: string;
  categoryId: string;
  categoryName: string | null;
  tagOptionIds: string[];
  authorCoachId: string | null;
  authorCoachName: string | null;
  authorCoachAvatarUrl: string | null;
  status: StudioContentStatus;
  version: number;
  createdBy: string;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  primaryLanguage: string;
  primaryTitle: string;
  languageCount: number;
  languageCodes: string[];
  tagCount: number;
}

interface BlogListResponse {
  items: BlogListItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export default function BlogListPage() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const isCoachOnly =
    !!user?.roles?.includes("coach") &&
    !user.roles.some((r) => ELEVATED_ROLES.includes(r));
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterValues, setFilterValues] = useState<Record<string, string>>({
    status: "all",
    category: "all",
    tag: "all",
    author: "all",
  });
  const [languageFilter, setLanguageFilter] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterValues, languageFilter]);

  const queryParams = useMemo(
    () => ({
      search: searchQuery || undefined,
      status:
        filterValues.status !== "all"
          ? (filterValues.status as StudioContentStatus)
          : undefined,
      categoryId:
        filterValues.category !== "all" ? filterValues.category : undefined,
      tagId: filterValues.tag !== "all" ? filterValues.tag : undefined,
      authorCoachId:
        filterValues.author !== "all" ? filterValues.author : undefined,
      language: languageFilter !== "all" ? languageFilter : undefined,
      page,
      limit: PAGE_SIZE,
    }),
    [searchQuery, filterValues, languageFilter, page],
  );

  const { data: blogResponse, isLoading } = useQuery<BlogListResponse>({
    queryKey: ["/content-blogs", queryParams],
    queryFn: () =>
      ContentBlogService.findAll(
        queryParams,
      ) as unknown as Promise<BlogListResponse>,
  });
  const blogItems = blogResponse?.items ?? [];
  const totalItems = blogResponse?.pagination.total ?? 0;
  const totalPages = blogResponse?.pagination.totalPages ?? 1;

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/content-category"],
    queryFn: () => categoryService.findAll(),
  });

  const { data: tagGroups = [] } = useQuery<TagGroup[]>({
    queryKey: ["/content/tag-groups"],
    queryFn: () => ContentTagService.getTagGroups(),
  });

  const { data: tagOptions = [] } = useQuery<TagOption[]>({
    queryKey: ["/content/tag-options"],
    queryFn: () => ContentTagService.getTagOptions(),
  });

  const { data: coaches = [] } = useQuery<Coach[]>({
    queryKey: ["/api/coaches"],
    queryFn: () => coachService.getCoaches().then((r) => r.coaches),
    enabled: !isCoachOnly,
  });

  const { submitMutation, archiveMutation, withdrawMutation, restoreMutation } =
    useContentLifecycle({
      contentType: "blog",
      apiPath: "/content-blogs",
      listRoute: "/studio/blogs",
      editRoute: (id) => `/studio/blogs/${id}/edit`,
    });

  const tagOptionsWithGroup = tagOptions.map((opt) => {
    const group = tagGroups.find((g) => g.id === opt.groupId);
    return { ...opt, groupName: group?.name ?? "" };
  });

  const handleFilterChange = (key: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const customFilters = [
    {
      key: "category",
      label: "Categories",
      options: categories.map((cat) => ({ value: cat.id, label: cat.title })),
    },
    {
      key: "tag",
      label: "Tags",
      options: tagOptionsWithGroup.map((opt) => ({
        value: opt.id,
        label: opt.groupName ? `${opt.groupName}: ${opt.label}` : opt.label,
      })),
    },
    ...(isCoachOnly
      ? []
      : [
          {
            key: "author",
            label: "Authors",
            width: "160px",
            options: coaches.map((coach) => ({
              value: coach.id,
              label: coach.name,
            })),
          },
        ]),
  ];

  const startIndex = totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + blogItems.length, totalItems);

  return (
    <ContentListShell
      contentTypeConfig={blogConfig}
      isLoading={isLoading}
      itemCount={totalItems}
      searchQuery={searchInput}
      onSearchChange={setSearchInput}
      filters={customFilters}
      filterValues={filterValues}
      onFilterChange={handleFilterChange}
      showLanguageFilter={true}
      languageFilterValue={languageFilter}
      onLanguageFilterChange={setLanguageFilter}
      pagination={{
        currentPage: page,
        totalPages,
        totalItems,
        startIndex,
        endIndex,
        canGoNext: page < totalPages,
        canGoPrev: page > 1,
        onPageChange: setPage,
        onNext: () => setPage((p) => Math.min(p + 1, totalPages)),
        onPrev: () => setPage((p) => Math.max(p - 1, 1)),
      }}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[300px]">Title</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Author</TableHead>
            <TableHead className="text-center">Languages</TableHead>
            <TableHead className="text-center">Tags</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead className="w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {blogItems.map((item) => {
            const statusConfig = STATUS_CONFIG[item.status];
            return (
              <TableRow
                key={item.id}
                className="cursor-pointer"
                onClick={() => setLocation(`/studio/blogs/${item.id}/edit`)}
                data-testid={`row-blog-${item.id}`}
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-muted shrink-0">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <p
                        className="font-medium text-foreground truncate"
                        data-testid={`text-blog-title-${item.id}`}
                      >
                        {item.primaryTitle}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        v{item.version}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">
                    {item.categoryName || "—"}
                  </span>
                </TableCell>
                <TableCell>
                  {item.authorCoachName ? (
                    <div className="flex items-center gap-2">
                      <Avatar className="h-6 w-6">
                        {item.authorCoachAvatarUrl ? (
                          <AvatarImage
                            src={item.authorCoachAvatarUrl}
                            alt={item.authorCoachName}
                          />
                        ) : null}
                        <AvatarFallback className="text-[10px]">
                          {item.authorCoachName.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm text-muted-foreground">
                        {item.authorCoachName}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <User className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {"—"}
                      </span>
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {item.languageCount}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Tags className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {item.tagCount}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    variant={statusConfig.variant}
                    className="no-default-hover-elevate no-default-active-elevate"
                  >
                    {statusConfig.label}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">
                    {formatDate(item.updatedAt)}
                  </span>
                </TableCell>
                <TableCell>
                  <LifecycleActionsMenu
                    itemId={item.id}
                    status={item.status}
                    createdBy={item.createdBy}
                    authorCoachId={item.authorCoachId}
                    onEdit={(id) => setLocation(`/studio/blogs/${id}/edit`)}
                    onSubmit={(id) => submitMutation.mutate(id)}
                    onArchive={(id) => archiveMutation.mutate(id)}
                    onWithdraw={(id) => withdrawMutation.mutate(id)}
                    onRestore={(id) => restoreMutation.mutate(id)}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </ContentListShell>
  );
}
